/**
 * Pure mapper from unwrapped single-order domain values into the prop bag
 * consumed by {@link HeaderSingleOrder}.
 *
 * Keeping this as a side-effect-free helper makes it trivial to unit-test the
 * "unwrap → header props" mapping without standing up the React tree.
 *
 * @module buildHeaderProps
 */
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { formatDateToUTCWithFormat } from "../../../utils/formatDate";
import { rotateLastSegmentFirst } from "../../../utils/getFullNameOfTheProvider";

interface BuildHeaderPropsArgs {
  order: GeneralOrderDetails | null;
  authOnFile: AuthOnFile | null;
  coverages: SingleCoverage[] | null;
  submission: Submission | null;
  hideButton: boolean | undefined;
  refetchAllData: () => void;
}

/** Prop bag for {@link HeaderSingleOrder}. */
export interface HeaderPropsBag {
  patientName: string;
  patientMrn: string | undefined;
  location: string;
  authorizationId: string;
  dateOfService: string;
  status: string | undefined;
  orderId: string;
  hideButton: boolean | undefined;
  orderData: GeneralOrderDetails | undefined;
  providerName: string;
  insuranceName: string;
  authOnFileDetails: AuthOnFile | undefined;
  coverageDetails: SingleCoverage[] | undefined;
  submissionData: Submission | undefined;
  refetchAllData: () => void;
}

/**
 * Builds the prop bag for {@link HeaderSingleOrder} from the unwrapped
 * single-order data, preserving the exact shape of the original inline prop
 * expressions in `singleOrderLayout.tsx` (including the legacy unguarded
 * `first_name + " " + last_name` concat).
 *
 * @remarks `dateOfService` uses {@link formatDateToUTCWithFormat} so the header
 *   shows the same calendar day as the API's UTC-midnight ISO strings in every
 *   viewer timezone (avoids local `moment` shifting DOS west of UTC).
 */
export const buildHeaderProps = ({
  order,
  authOnFile,
  coverages,
  submission,
  hideButton,
  refetchAllData,
}: BuildHeaderPropsArgs): HeaderPropsBag => ({
  patientName:
    (order?.demographics?.first_name as string) +
    " " +
    (order?.demographics?.last_name as string),
  patientMrn:
    order?.demographics?.patient_id ?? order?.demographics?.patient_mrn,
  location: order?.payload?.location ?? "N/A",
  authorizationId: "N/A",
  dateOfService: formatDateToUTCWithFormat(
    order?.payload?.date_of_service ?? "",
    "MM/DD/YYYY",
    "",
  ),
  status: authOnFile?.auth_status,
  orderId: order?.order_id ?? "",
  hideButton,
  orderData: order ?? undefined,
  providerName: rotateLastSegmentFirst(order?.payload?.practitioner_name ?? ""),
  insuranceName:
    coverages?.find((coverage) => coverage.coverage_type === "primary")
      ?.payer_name ?? "",
  authOnFileDetails: authOnFile ?? undefined,
  coverageDetails: coverages ?? undefined,
  submissionData: submission ?? undefined,
  refetchAllData,
});
